import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, FileText, GraduationCap, CreditCard, 
  Settings, TrendingUp, Clock, CheckCircle,
  DollarSign, Calendar, Activity, ArrowRight,
  Download, BarChart3, UserCheck, Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/**
 * COMPLETE ADMIN DASHBOARD
 * 
 * Central hub for all admin operations:
 * - Applications management
 * - Tutor directory
 * - Student enrollments
 * - Payment tracking
 * - System settings
 */
const AdminDashboardComplete = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    pendingApplications: 0,
    totalTutors: 0,
    activeStudents: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    recentApplications: [],
    recentPayments: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      // Fetch applications
      const appsRes = await fetch(`${BACKEND_URL}/api/admin/fresh-applications`);
      const appsData = await appsRes.json();
      
      const applications = appsData.applications || [];
      const pendingApps = applications.filter(a => a.status === 'Pending Review');
      
      // Fetch tutors
      const tutorsRes = await fetch(`${BACKEND_URL}/api/tutors`);
      const tutorsData = await tutorsRes.json();
      const tutors = tutorsData.tutors || [];
      
      // Fetch students (if endpoint exists)
      let studentCount = 0;
      try {
        const studentsRes = await fetch(`${BACKEND_URL}/api/admin/student-enrollments`);
        const studentsData = await studentsRes.json();
        studentCount = studentsData.enrollments?.length || 0;
      } catch (e) {
        console.log('Student data not available');
      }
      
      // Mock payment data (will be real when Stripe is integrated)
      const mockRevenue = 12450;
      const mockPendingPayments = 3;
      const mockRecentPayments = [
        { id: 1, student: 'Sarah Johnson', amount: 250, date: '2025-12-12', status: 'Completed' },
        { id: 2, student: 'Michael Chen', amount: 180, date: '2025-12-11', status: 'Completed' },
        { id: 3, student: 'Emma Davis', amount: 300, date: '2025-12-10', status: 'Pending' }
      ];
      
      setStats({
        pendingApplications: pendingApps.length,
        totalTutors: tutors.length,
        activeStudents: studentCount,
        totalRevenue: mockRevenue,
        pendingPayments: mockPendingPayments,
        recentApplications: applications.slice(0, 5),
        recentPayments: mockRecentPayments
      });
      
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color, onClick }) => (
    <Card 
      className={`p-6 hover:shadow-lg transition-all cursor-pointer ${onClick ? 'hover:scale-105' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold mb-2" style={{ color }}>{value}</p>
          {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
        </div>
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
      </div>
    </Card>
  );

  const QuickActionCard = ({ icon: Icon, title, description, route, color }) => (
    <Card 
      className="p-6 hover:shadow-lg transition-all cursor-pointer hover:scale-105"
      onClick={() => navigate(route)}
    >
      <div className="flex items-start gap-4">
        <div 
          className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-6 h-6" style={{ color }} />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg mb-1">{title}</h3>
          <p className="text-sm text-gray-600 mb-3">{description}</p>
          <div className="flex items-center gap-2 text-sm font-medium" style={{ color }}>
            Go to {title} <ArrowRight className="w-4 h-4" />
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      
      {/* Header */}
      <div className="premium-gradient text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="heading-lg mb-2 flex items-center gap-3">
                <Activity className="w-8 h-8" />
                Admin Dashboard
              </h1>
              <p className="opacity-90">Complete overview of Winhouse Tutors operations</p>
            </div>
            <Button
              onClick={() => navigate('/')}
              className="bg-white/20 hover:bg-white/30 backdrop-blur"
            >
              View Site
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Key Stats */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={Clock}
            title="Pending Applications"
            value={loading ? '...' : stats.pendingApplications}
            subtitle="Awaiting review"
            color="rgb(234, 179, 8)"
            onClick={() => navigate('/admin/applications')}
          />
          <StatCard
            icon={GraduationCap}
            title="Active Tutors"
            value={loading ? '...' : stats.totalTutors}
            subtitle="In tutor directory"
            color="rgb(59, 130, 246)"
            onClick={() => navigate('/tutors')}
          />
          <StatCard
            icon={Users}
            title="Active Students"
            value={loading ? '...' : stats.activeStudents}
            subtitle="Enrolled students"
            color="rgb(16, 185, 129)"
            onClick={() => navigate('/admin/students')}
          />
          <StatCard
            icon={DollarSign}
            title="Total Revenue"
            value={loading ? '...' : `$${stats.totalRevenue.toLocaleString()}`}
            subtitle={`${stats.pendingPayments} pending payments`}
            color="rgb(168, 85, 247)"
            onClick={() => navigate('/admin/payments')}
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="heading-md mb-4">Quick Actions</h2>
          <div className="grid md:grid-cols-2 gap-6">
            
            <QuickActionCard
              icon={FileText}
              title="Tutor Applications"
              description="Review and approve tutor applications, download CVs"
              route="/admin/applications"
              color="var(--accent-gold)"
            />
            
            <QuickActionCard
              icon={GraduationCap}
              title="Tutor Directory"
              description="View all tutors, edit profiles, manage availability"
              route="/tutors"
              color="rgb(59, 130, 246)"
            />
            
            <QuickActionCard
              icon={Users}
              title="Student Management"
              description="View enrollments, assign tutors, track progress"
              route="/admin/students"
              color="rgb(16, 185, 129)"
            />
            
            <QuickActionCard
              icon={CreditCard}
              title="Payment Tracking"
              description="Monitor payments, process refunds, view revenue"
              route="/admin/payments"
              color="rgb(168, 85, 247)"
            />
            
            <QuickActionCard
              icon={Package}
              title="Lesson Packages"
              description="Manage pricing (legacy display only — Paystack is the active payment path)"
              route="/admin/payments"
              color="rgb(249, 115, 22)"
            />
            
            <QuickActionCard
              icon={Settings}
              title="System Settings"
              description="Migrations, database management, configurations"
              route="/admin/migrations"
              color="rgb(100, 116, 139)"
            />
            
          </div>
        </div>

        {/* Recent Activity */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          
          {/* Recent Applications */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading-md flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Recent Applications
              </h3>
              <Button
                onClick={() => navigate('/admin/applications')}
                variant="ghost"
                size="sm"
              >
                View All
              </Button>
            </div>
            
            {loading ? (
              <p className="text-gray-500 text-sm">Loading...</p>
            ) : stats.recentApplications.length === 0 ? (
              <p className="text-gray-500 text-sm">No applications yet</p>
            ) : (
              <div className="space-y-3">
                {stats.recentApplications.map(app => (
                  <div key={app.application_id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                    <div className="flex-1">
                      <p className="font-medium text-sm">{app.full_name}</p>
                      <p className="text-xs text-gray-600">
                        {app.subjects.slice(0, 2).join(', ')}
                        {app.subjects.length > 2 ? ` +${app.subjects.length - 2}` : ''}
                      </p>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      app.status === 'Pending Review' ? 'bg-yellow-100 text-yellow-800' :
                      app.status === 'Approved' ? 'bg-green-100 text-green-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {app.status}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Recent Payments */}
          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading-md flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Recent Payments
              </h3>
              <Button
                onClick={() => navigate('/admin/payments')}
                variant="ghost"
                size="sm"
              >
                View All
              </Button>
            </div>
            
            <div className="space-y-3">
              {stats.recentPayments.map(payment => (
                <div key={payment.id} className="flex items-center justify-between p-3 bg-gray-50 rounded">
                  <div className="flex-1">
                    <p className="font-medium text-sm">{payment.student}</p>
                    <p className="text-xs text-gray-600">{payment.date}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-sm">${payment.amount}</p>
                    <span className={`text-xs ${
                      payment.status === 'Completed' ? 'text-green-600' : 'text-yellow-600'
                    }`}>
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm text-blue-900">
              <p className="font-medium mb-1">💳 Stripe Integration Pending</p>
              <p className="text-xs">Payment data shown above is placeholder. Real-time payments will appear here once Stripe is integrated.</p>
            </div>
          </Card>
          
        </div>

        {/* Revenue Overview */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="heading-md flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Revenue Overview
            </h3>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
          
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">This Month</p>
              <p className="text-2xl font-bold text-purple-600">$4,250</p>
              <p className="text-xs text-green-600 mt-1">↑ 12% vs last month</p>
            </div>
            
            <div className="text-center p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">This Quarter</p>
              <p className="text-2xl font-bold text-blue-600">$12,450</p>
              <p className="text-xs text-green-600 mt-1">↑ 8% vs last quarter</p>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Avg. Package Value</p>
              <p className="text-2xl font-bold text-green-600">$285</p>
              <p className="text-xs text-gray-500 mt-1">Based on 15 sales</p>
            </div>
            
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Pending Payouts</p>
              <p className="text-2xl font-bold text-orange-600">$850</p>
              <p className="text-xs text-gray-500 mt-1">3 tutors</p>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-900">
            <p className="font-medium mb-1">⚠️ Placeholder Data</p>
            <p className="text-xs">Revenue figures shown are examples. Real payment data will sync automatically once Stripe webhooks are configured.</p>
          </div>
        </Card>

      </div>
    </div>
  );
};

export default AdminDashboardComplete;
